/**
 * Typed data-import targets — the reusable "import a CSV as X" pattern.
 *
 * A capability that wants a first-class file import declares ONE
 * `ImportTargetSpec` here: the target `kind` (the server importer key in
 * `excellent/src/data-import/registry.ts`), the human labels, and the set of
 * fields a CSV/TSV/JSON column can map onto. This is pure data — no server
 * imports — so the wizard (client), the generic route (server, for the field
 * allowlist), and the `/database/import` hub all read the same source of truth.
 *
 * Field `value`s are dotted target paths the matching server importer knows how
 * to land (e.g. `person.primary_email`, `organization.name`). `match` is the
 * set of lowercase header substrings used to auto-guess a column → field
 * mapping on upload (see `guessMapping`), so the user usually confirms rather
 * than hand-maps.
 *
 * The set of kinds here MUST stay in lockstep with the server importer registry
 * — `pnpm check-data-import-lockstep` fails if a target has no importer or an
 * importer has no target.
 */

export const MAX_IMPORT_BYTES = 5_000_000;

export interface ImportFieldOption {
  /** Dotted target path the server importer lands (e.g. `person.primary_email`). */
  value: string;
  /** Label shown in the column-mapping dropdown. */
  label: string;
  /** Lowercase header substrings that auto-guess this field on upload. */
  match?: string[];
  /** Marks the field the importer needs to create a row (surfaced as a hint). */
  required?: boolean;
}

export interface ImportTargetSpec {
  /** Stable importer key — must match a `DataImporter.kind` on the server. */
  kind: string;
  /** Title shown above the wizard, e.g. "Import leads". */
  title: string;
  /** Plural noun for the rows, e.g. "leads" / "candidates" / "transactions". */
  noun: string;
  /** One-line description of where the rows land. */
  blurb: string;
  /** Max upload size in bytes. */
  maxBytes: number;
  /** Columns a CSV can map onto. */
  fields: ImportFieldOption[];
}

export const IMPORT_TARGETS: Record<string, ImportTargetSpec> = {
  "crm-leads": {
    kind: "crm-leads",
    title: "Import leads",
    noun: "leads",
    blurb:
      "Leads, people, and organizations all land via the Schema. Rows dedupe on email.",
    maxBytes: MAX_IMPORT_BYTES,
    fields: [
      { value: "lead.display_name", label: "Lead name", match: ["lead"] },
      { value: "lead.status_slug", label: "Lead status (slug)", match: ["status"] },
      { value: "lead.source_slug", label: "Lead source (slug)", match: ["source"] },
      {
        value: "person.display_name",
        label: "Contact name",
        match: ["contact name", "full name", "name"],
      },
      { value: "person.given_name", label: "Contact first name", match: ["first"] },
      { value: "person.family_name", label: "Contact last name", match: ["last", "surname"] },
      {
        value: "person.primary_email",
        label: "Contact email",
        match: ["email", "e-mail"],
      },
      {
        value: "organization.name",
        label: "Organization name",
        match: ["company", "organization", "organisation", "account"],
      },
      {
        value: "organization.website",
        label: "Organization website",
        match: ["website", "url", "domain"],
      },
    ],
  },
  "hiring-candidates": {
    kind: "hiring-candidates",
    title: "Import candidates",
    noun: "candidates",
    blurb:
      "Candidates are people. Rows dedupe on email and appear in the Candidates list.",
    maxBytes: MAX_IMPORT_BYTES,
    fields: [
      {
        value: "person.display_name",
        label: "Candidate name",
        match: ["candidate", "full name", "name"],
        required: true,
      },
      { value: "person.given_name", label: "First name", match: ["first"] },
      { value: "person.family_name", label: "Last name", match: ["last", "surname"] },
      {
        value: "person.primary_email",
        label: "Email",
        match: ["email", "e-mail"],
      },
      { value: "person.phone", label: "Phone", match: ["phone", "mobile", "tel"] },
      {
        value: "candidate.note",
        label: "Note (added to profile)",
        match: ["note", "comment", "summary", "headline"],
      },
    ],
  },
  "finance-transactions": {
    kind: "finance-transactions",
    title: "Import transactions",
    noun: "transactions",
    blurb:
      "Money movements land under a ledger account (resolved by name). Rows dedupe on the external id.",
    maxBytes: MAX_IMPORT_BYTES,
    fields: [
      {
        value: "account.name",
        label: "Account name",
        match: ["account", "ledger"],
        required: true,
      },
      {
        value: "transaction.amount",
        label: "Amount",
        match: ["amount", "value", "total", "debit", "credit"],
        required: true,
      },
      {
        value: "transaction.occurred_at",
        label: "Date",
        match: ["date", "posted", "occurred", "time"],
      },
      { value: "transaction.currency", label: "Currency", match: ["currency", "ccy"] },
      {
        value: "transaction.memo",
        label: "Memo / description",
        match: ["memo", "description", "note", "detail", "narrative"],
      },
      {
        value: "transaction.external_id",
        label: "External id (for dedupe)",
        match: ["id", "reference", "ref", "txn"],
      },
    ],
  },
};

export function getImportTarget(kind: string): ImportTargetSpec | undefined {
  return IMPORT_TARGETS[kind];
}

export function listImportTargets(): ImportTargetSpec[] {
  return Object.values(IMPORT_TARGETS);
}

/**
 * Auto-guess a column → field mapping from header names. For each column, pick
 * the not-yet-used field with the best `match` keyword: LONGEST wins (most
 * specific), ties broken by the EARLIEST position in the header. So "First Name"
 * → `person.given_name` ("first" 5 > "name" 4) and "Last Name" →
 * `person.family_name` ("last" and "name" both 4, but "last" comes first), while
 * a bare "Name" still → `person.display_name`. Returns `{ [column]: fieldValue }`;
 * unmatched columns are omitted (the user maps them by hand).
 */
export function guessMapping(
  columns: string[],
  target: ImportTargetSpec,
): Record<string, string> {
  const used = new Set<string>();
  const out: Record<string, string> = {};
  for (const col of columns) {
    const lower = col.trim().toLowerCase();
    if (!lower) continue;
    let best: string | null = null;
    let bestLen = 0;
    let bestPos = Infinity;
    for (const field of target.fields) {
      if (used.has(field.value)) continue;
      for (const m of field.match ?? []) {
        const pos = lower.indexOf(m);
        if (pos === -1) continue;
        if (m.length > bestLen || (m.length === bestLen && pos < bestPos)) {
          best = field.value;
          bestLen = m.length;
          bestPos = pos;
        }
      }
    }
    if (best) {
      out[col] = best;
      used.add(best);
    }
  }
  return out;
}
